import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type LeadSource = 'website' | 'facebook' | 'instagram' | 'manual' | 'other';
 export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'demo_scheduled' | 'converted' | 'lost' | 'unsubscribed';

export interface Lead {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  establishment_name?: string;
  establishment_type?: string;
  seating_capacity?: number;
  city?: string;
  source: LeadSource;
  source_campaign?: string;
  source_details?: Record<string, unknown> | null;
  status: LeadStatus;
  interested_plan?: string;
  score: number;
  notes?: string;
  message?: string;
  contact_preference?: string;
  contacted_at?: string;
  contacted_by?: string;
  converted_at?: string;
  converted_to_establishment_id?: string;
  created_at: string;
  updated_at: string;
  // Champs de qualification Meta Lead Ads
  current_booking_channels?: string[] | null;
  current_management_system?: string[] | null;
  // Email tracking fields
  last_email_sent_at?: string;
  last_email_type?: string;
  email_count: number;
}

export interface LeadFilters {
  source?: LeadSource | 'all';
  status?: LeadStatus | 'all';
  period?: '7d' | '30d' | '90d' | 'all';
  search?: string;
}

export interface LeadStats {
  total: number;
  newThisWeek: number;
  bySource: Record<LeadSource, number>;
  byStatus: Record<LeadStatus, number>;
}

export function useAdminLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<LeadFilters>({
    source: 'all',
    status: 'all',
    period: '30d',
    search: '',
  });
  const { toast } = useToast();

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.source && filters.source !== 'all') {
        query = query.eq('source', filters.source);
      }

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.period && filters.period !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (filters.period) {
          case '7d':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '90d':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = new Date(0);
        }
        
        query = query.gte('created_at', startDate.toISOString());
      }

      if (filters.search) {
        query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,establishment_name.ilike.%${filters.search}%,city.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Cast the data to Lead type
      const typedLeads = (data || []).map(lead => ({
        ...lead,
        source: lead.source as LeadSource,
        status: lead.status as LeadStatus,
      })) as Lead[];

      setLeads(typedLeads);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les prospects",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('id, source, status, created_at');

      if (error) throw error;

      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const bySource: Record<LeadSource, number> = {
        website: 0,
        facebook: 0,
        instagram: 0,
        manual: 0,
        other: 0,
      };

      const byStatus: Record<LeadStatus, number> = {
        new: 0,
        contacted: 0,
        qualified: 0,
        demo_scheduled: 0,
        converted: 0,
        lost: 0,
        unsubscribed: 0,
      };

      let newThisWeek = 0;

      (data || []).forEach(lead => {
        const source = lead.source as LeadSource;
        const status = lead.status as LeadStatus;
        
        if (bySource[source] !== undefined) {
          bySource[source]++;
        }
        
        if (byStatus[status] !== undefined) {
          byStatus[status]++;
        }

        if (new Date(lead.created_at) >= oneWeekAgo) {
          newThisWeek++;
        }
      });

      setStats({
        total: data?.length || 0,
        newThisWeek,
        bySource,
        byStatus,
      });
    } catch (error) {
      console.error('Error fetching lead stats:', error);
    }
  }, []);

  const updateLeadStatus = async (leadId: string, newStatus: LeadStatus) => {
    try {
      const updates: Record<string, string> = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };

      if (newStatus === 'contacted') {
        updates.contacted_at = new Date().toISOString();
      } else if (newStatus === 'converted') {
        updates.converted_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('leads')
        .update(updates)
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: "Statut mis à jour",
        description: `Le prospect a été marqué comme "${getStatusLabel(newStatus)}"`,
      });

      await fetchLeads();
      await fetchStats();
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive",
      });
    }
  };

  const updateLeadNotes = async (leadId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ notes, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: "Notes sauvegardées",
      });

      await fetchLeads();
    } catch (error) {
      console.error('Error updating lead notes:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les notes",
        variant: "destructive",
      });
    }
  };

  const addManualLead = async (leadData: Partial<Lead>) => {
    try {
      const { error } = await supabase
        .from('leads')
        .insert({
          full_name: leadData.full_name || '',
          email: leadData.email || '',
          phone: leadData.phone,
          establishment_name: leadData.establishment_name,
          city: leadData.city,
          source: 'manual' as LeadSource,
          status: 'new' as LeadStatus,
          notes: leadData.notes,
        });

      if (error) throw error;

      toast({
        title: "Prospect ajouté",
        description: "Le nouveau prospect a été créé",
      });

      await fetchLeads();
      await fetchStats();
    } catch (error) {
      console.error('Error adding manual lead:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le prospect",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [fetchLeads, fetchStats]);

  const deleteLead = async (leadId: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: "Prospect supprimé",
        description: "Le prospect a été supprimé définitivement",
      });

      await fetchLeads();
      await fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le prospect",
        variant: "destructive",
      });
    }
  };

  const updateLead = async (leadId: string, leadData: Partial<Lead>) => {
    try {
      // Remove fields that shouldn't be sent to the database or have type issues
      const { id, source_details, ...updateData } = leadData;
      
      const { error } = await supabase
        .from('leads')
        .update({
          ...updateData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: "Prospect modifié",
        description: "Les informations ont été mises à jour",
      });

      await fetchLeads();
      await fetchStats();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le prospect",
        variant: "destructive",
      });
    }
  };


  return {
    leads,
    stats,
    loading,
    filters,
    setFilters,
    updateLeadStatus,
    updateLeadNotes,
    addManualLead,
    deleteLead,
    updateLead,
    refetch: fetchLeads,
  };
}

export function getStatusLabel(status: LeadStatus): string {
  const labels: Record<LeadStatus, string> = {
    new: 'Nouveau',
    contacted: 'Contacté',
    qualified: 'Qualifié',
    demo_scheduled: 'Démo planifiée',
    converted: 'Converti',
    lost: 'Perdu',
    unsubscribed: 'Désabonné',
  };
  return labels[status];
}

export function getSourceLabel(source: LeadSource): string {
  const labels: Record<LeadSource, string> = {
    website: 'Site web',
    facebook: 'Facebook',
    instagram: 'Instagram',
    manual: 'Manuel',
    other: 'Autre',
  };
  return labels[source];
}

export function getSourceIcon(source: LeadSource): string {
  const icons: Record<LeadSource, string> = {
    website: '🌐',
    facebook: '📘',
    instagram: '📷',
    manual: '✏️',
    other: '📋',
  };
  return icons[source];
}

export function getStatusColor(status: LeadStatus): string {
  const colors: Record<LeadStatus, string> = {
    new: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    contacted: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    qualified: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    demo_scheduled: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20',
    converted: 'bg-green-500/10 text-green-600 border-green-500/20',
    lost: 'bg-red-500/10 text-red-600 border-red-500/20',
    unsubscribed: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  };
  return colors[status];
}
